import type { UseFormGetValues } from 'react-hook-form';

import { useCallback, useEffect, useRef, useState } from 'react';

import { NewEmailFormModel } from '@/components/emailWindow/model/NewEmailForm.model.ts';
import { useMapNewEmailToUpdateDraft } from '@/components/emailWindow/util/mapNewEmailToUpdateDraft.util.ts';
import { useAnyAttachmentIsNotReady } from '@/components/emailWindow/util/useAnyAttachmentIsNotReady.util.ts';
import { useSendEmailFromDraft } from '@/components/emailWindow/util/useSendEmailFromDraft.util.ts';
import { useUpdateEmailDraft } from '@/components/emailWindow/util/useUpdateEmailDraft.util.ts';
import { CONFIG } from '@/config/config.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import {
  EditedEmailState,
  useNewEmailAutosavePaused,
  useNewEmailDraftControllerState,
} from '@/views/emails/context/NewEmails.ts';

type Params = {
  getFormValues: UseFormGetValues<NewEmailFormModel>;
};

type Return = {
  isSendingEmail: boolean;
  saveCurrentDraft: (mode?: SaveDraftMode) => Promise<boolean>;
  sendDraft: (formValues: NewEmailFormModel) => Promise<boolean>;
};

type SaveDraftMode = 'background' | 'final';

export const useEmailDraftController = ({ getFormValues }: Params): Return => {
  const autosavePaused = useNewEmailAutosavePaused();
  const draftControllerState = useNewEmailDraftControllerState();
  const { updateEmailDraft } = useUpdateEmailDraft();
  const { sendEmailFromDraft } = useSendEmailFromDraft();
  const { userId } = useUserContext();
  const { mapNewEmailToUpdateDraft } = useMapNewEmailToUpdateDraft();
  const anyAttachmentIsNotReady = useAnyAttachmentIsNotReady();
  const isComposerOpen = draftControllerState !== null;
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const autosavePausedRef = useRef(autosavePaused);
  const isSendingEmailRef = useRef(isSendingEmail);
  const isUpdatingDraftRef = useRef(false);
  const updateDraftPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    autosavePausedRef.current = autosavePaused;
  }, [autosavePaused]);

  useEffect(() => {
    isSendingEmailRef.current = isSendingEmail;
  }, [isSendingEmail]);

  const saveDraft = useCallback(
    async (formValues: NewEmailFormModel, mode: SaveDraftMode = 'background') => {
      if (
        !draftControllerState ||
        draftControllerState.state === EditedEmailState.IN_PROGRESS ||
        anyAttachmentIsNotReady ||
        (mode === 'background' && isSendingEmailRef.current)
      ) {
        return false;
      }

      if (isUpdatingDraftRef.current) {
        if (mode === 'background' || !updateDraftPromiseRef.current) {
          return false;
        }
        try {
          await updateDraftPromiseRef.current;
        } catch {
          // The following final save is authoritative for close/send.
        }
      }

      isUpdatingDraftRef.current = true;
      const updateDraftPromise = updateEmailDraft({
        data: mapNewEmailToUpdateDraft({
          form: formValues,
          onEmailId: draftControllerState.onEmailId,
          sendMode: draftControllerState.sendMode,
        }),
        emailId: draftControllerState.emailId,
        threadId: draftControllerState.threadId,
        userId,
      });
      updateDraftPromiseRef.current = updateDraftPromise;
      try {
        await updateDraftPromise;
        return true;
      } finally {
        isUpdatingDraftRef.current = false;
        if (updateDraftPromiseRef.current === updateDraftPromise) {
          updateDraftPromiseRef.current = null;
        }
      }
    },
    [
      anyAttachmentIsNotReady,
      draftControllerState,
      mapNewEmailToUpdateDraft,
      updateEmailDraft,
      userId,
    ],
  );

  const saveCurrentDraft = useCallback(
    async (mode: SaveDraftMode = 'background') => {
      return saveDraft(getFormValues(), mode);
    },
    [getFormValues, saveDraft],
  );

  const saveCurrentDraftRef = useRef(saveCurrentDraft);

  useEffect(() => {
    saveCurrentDraftRef.current = saveCurrentDraft;
  }, [saveCurrentDraft]);

  const runBackgroundSave = useCallback(() => {
    void saveCurrentDraftRef.current('background').catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!isComposerOpen) {
      return;
    }
    const intervalId = setInterval(() => {
      if (autosavePausedRef.current) {
        return;
      }
      runBackgroundSave();
    }, Number(CONFIG.EMAIL_DRAFT_AUTOSAVE));

    return () => {
      clearInterval(intervalId);
    };
  }, [isComposerOpen, runBackgroundSave]);

  useEffect(() => {
    if (autosavePaused) {
      runBackgroundSave();
    }
  }, [autosavePaused, runBackgroundSave]);

  useEffect(() => {
    return () => {
      if (isSendingEmailRef.current) {
        return;
      }
      runBackgroundSave();
    };
  }, [runBackgroundSave]);

  const sendDraft = useCallback(
    async (formValues: NewEmailFormModel) => {
      if (
        isSendingEmailRef.current ||
        !draftControllerState ||
        draftControllerState.state === EditedEmailState.IN_PROGRESS ||
        anyAttachmentIsNotReady
      ) {
        return false;
      }

      isSendingEmailRef.current = true;
      setIsSendingEmail(true);
      let sent = false;
      try {
        const wasDraftSaved = await saveDraft(formValues, 'final');
        if (!wasDraftSaved) {
          return false;
        }
        await sendEmailFromDraft({
          draftId: draftControllerState.emailId,
          threadId: draftControllerState.threadId,
          userId,
        });
        sent = true;
        return true;
      } catch {
        return false;
      } finally {
        if (!sent) {
          isSendingEmailRef.current = false;
          setIsSendingEmail(false);
        }
      }
    },
    [anyAttachmentIsNotReady, draftControllerState, saveDraft, sendEmailFromDraft, userId],
  );

  return {
    isSendingEmail,
    saveCurrentDraft,
    sendDraft,
  };
};
